import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Heart, Trash2, Download, Globe, Package, Loader2 } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, fileUrl, downloadImage } from "@/lib/api";

const STATUS = { awaiting_payment: "Awaiting payment", in_production: "In production", shipped: "Shipped", fulfillment_error: "Needs attention", awaiting_approval: "Being reviewed", canceled: "Canceled" };

export default function Gallery() {
  const [gens, setGens] = useState(null);
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("all");

  useEffect(() => {
    api.get("/generations").then((r) => setGens(r.data));
    api.get("/orders").then((r) => setOrders(r.data));
  }, []);

  const remove = async (id) => {
    await api.delete(`/generations/${id}`);
    setGens(gens.filter((g) => g.generation_id !== id));
    toast.success("Creation deleted");
  };

  const shown = (gens || []).filter((g) => tab === "all" || (tab === "favorites" && g.is_favorite) || (tab === "shared" && g.is_public));

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="gallery-page">
      <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">My Gallery</h1>
      <Tabs value={tab} onValueChange={setTab} className="mt-8">
        <TabsList className="rounded-full bg-white border border-[#EAE3D9] p-1 h-auto">
          {["all", "favorites", "shared", "orders"].map((t) => (
            <TabsTrigger key={t} value={t} data-testid={`gallery-tab-${t}`} className="rounded-full capitalize px-5 py-1.5 font-bold data-[state=active]:bg-[#1E1B26] data-[state=active]:text-white">{t}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {tab === "orders" ? (
        <div className="mt-8 space-y-3" data-testid="orders-list">
          {orders.length === 0 && <p className="text-[#6E687A]">No orders yet. Open any creation and tap "Order a product".</p>}
          {orders.map((o) => (
            <div key={o.order_id} className="flex items-center gap-4 rounded-2xl bg-white border border-[#EAE3D9] p-4" data-testid={`order-row-${o.order_id}`}>
              <img src={fileUrl(o.image_path)} alt="" className="h-16 w-16 rounded-xl object-cover" />
              <div className="flex-1 min-w-0"><p className="font-bold">{o.product_name}{o.size ? ` · ${o.size}` : ""}{o.color ? ` · ${o.color}` : ""}</p><p className="text-xs font-mono text-[#6E687A]">{o.order_id}</p>
                {o.tracking?.filter((t) => t.url).map((t) => <a key={t.number} href={t.url} target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-[#FF5A79]" data-testid={`order-tracking-${o.order_id}`}>Track {t.carrier} {t.number}</a>)}
              </div>
              <span className="rounded-full bg-[#F3EFFF] text-[#8A4FFF] px-3 py-1 text-xs font-bold flex items-center gap-1"><Package className="h-3.5 w-3.5" />{STATUS[o.status] || o.status}</span>
              <span className="font-mono font-bold">${o.price.toFixed(2)}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {gens === null && [0, 1, 2, 3].map((i) => <div key={i} className="shimmer aspect-square rounded-3xl" />)}
          {gens && shown.length === 0 && (
            <div className="col-span-full rounded-3xl border-2 border-dashed border-[#EAE3D9] p-12 text-center" data-testid="gallery-empty">
              <p className="font-display text-2xl font-semibold">Nothing here yet</p>
              <Link to="/studio" className="mt-4 inline-block rounded-full bg-[#FF5A79] text-white px-6 py-2.5 font-bold sticker sticker-press" data-testid="gallery-create-button">Create something cute</Link>
            </div>
          )}
          {shown.map((g) => (
            <div key={g.generation_id} className="group relative rounded-3xl overflow-hidden bg-white border border-[#EAE3D9]" data-testid={`gallery-item-${g.generation_id}`}>
              <Link to={`/creation/${g.generation_id}`}>
                {g.status === "completed" ? <img src={fileUrl(g.output_path)} alt="" className="aspect-square w-full object-cover" />
                  : <div className={`aspect-square grid place-items-center ${g.status === "processing" ? "shimmer" : "bg-[#F5F0E8]"}`}>{g.status === "processing" ? <Loader2 className="h-6 w-6 animate-spin text-[#FF5A79]" /> : <span className="text-sm font-semibold text-[#6E687A]">Failed</span>}</div>}
              </Link>
              <div className="p-3">
                <p className="font-display font-semibold truncate">{g.pet_name} · {g.prompt_title}</p>
                <div className="mt-2 flex items-center gap-2 text-[#6E687A]">
                  {g.is_favorite && <Heart className="h-4 w-4 fill-[#FF5A79] text-[#FF5A79]" />}
                  {g.is_public && <Globe className="h-4 w-4 text-[#8A4FFF]" />}
                  <div className="ml-auto flex gap-1">
                    {g.status === "completed" && <button onClick={() => downloadImage(g.output_path, `${g.pet_name}.png`).catch(() => toast.error("Download failed, please try again"))} className="p-1.5 rounded-full hover:bg-[#F5F0E8]" data-testid={`gallery-download-${g.generation_id}`} aria-label="Download"><Download className="h-4 w-4" /></button>}
                    <button onClick={() => remove(g.generation_id)} className="p-1.5 rounded-full hover:bg-red-50" data-testid={`gallery-delete-${g.generation_id}`} aria-label="Delete"><Trash2 className="h-4 w-4 text-red-500" /></button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </main>
  );
}
